<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import { RoutingView } from '@tundr/routing';
  import { useServiceWorkerUpdate } from '@tundr/service-worker';
  import {
    Sidebar,
    SidebarNav,
    SidebarVariant,
    type SidebarItemProps,
  } from '@tundr/sidebar';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { PageLayout } from '@tundr/ui-kit';
  import { computed } from 'vue';
  import { appRoutesDictionary } from '@bo/core/router/routes.dictionary.ts';
  import UserMenu from '@bo/shared/components/UserMenu/UserMenu.vue';
  import { sidebarHeaderSectionClass } from './AppRouter.css';

  const { t } = useLocale('common');

  const { needRefresh, applyUpdate } = useServiceWorkerUpdate();

  const items = computed<SidebarItemProps[]>(() => [
    {
      label: t('sections.general'),
      icon: TUNDR_ICONS.HOME,
      subItems: [
        {
          label: t('sections.users'),
          to: appRoutesDictionary.users.root,
          testId: 'users',
        },
        {
          label: t('sections.companies'),
          to: appRoutesDictionary.company.root,
          testId: 'companies',
        },
      ],
    },
    {
      label: t('sections.tundr_card'),
      icon: TUNDR_ICONS.CARD,
      subItems: [
        {
          label: t('sections.payments'),
          to: appRoutesDictionary.payments.root,
          testId: 'payments',
        },
        {
          label: t('sections.card_orders'),
          to: appRoutesDictionary.cardOrder.root,
          testId: 'card_orders',
        },
      ],
    },
    {
      label: t('sections.meal_vouchers'),
      icon: TUNDR_ICONS.UTENSILS,
      subItems: [
        {
          label: t('sections.meal_vouchers_movements'),
          to: appRoutesDictionary.mealVouchers.movements,
          testId: 'meal_vouchers_movements',
        },
        {
          label: t('sections.meal_vouchers_card_orders'),
          to: appRoutesDictionary.mealVouchers.cardOrders,
          testId: 'meal_vouchers_card_orders',
        },
        {
          label: t('sections.meal_vouchers_bank_transfers'),
          to: appRoutesDictionary.mealVouchers.bankTransfers,
          testId: 'meal_vouchers_bank_transfers',
        },
      ],
    },
    {
      label: t('sections.fringe_merchants'),
      icon: TUNDR_ICONS.STORE,
      subItems: [
        {
          label: t('sections.search_mid'),
          to: appRoutesDictionary.fringe.searchMid,
          testId: 'search_mid',
        },
        {
          label: t('sections.update_mid'),
          to: appRoutesDictionary.fringe.updateMid,
          testId: 'update_mid',
        },
        {
          label: t('sections.keywords'),
          to: appRoutesDictionary.paymentValidationKeywords.root,
          testId: 'keywords',
        },
        {
          label: t('sections.brands'),
          to: appRoutesDictionary.brands.root,
          testId: 'brands',
        },
      ],
    },
    {
      label: t('sections.billing'),
      icon: TUNDR_ICONS.RECEIPT,
      subItems: [
        {
          label: t('sections.cards_shipped'),
          to: appRoutesDictionary.reports.root,
          testId: 'cards_shipped',
        },
        {
          label: t('sections.subscriptions'),
          to: appRoutesDictionary.subscription.root,
          testId: 'subscriptions',
        },
        {
          label: t('sections.fringe_orders'),
          to: appRoutesDictionary.billing.fringeOrders,
          testId: 'fringe_orders',
        },
        {
          label: t('sections.reversals'),
          to: { name: appRoutesDictionary.reversal.list.name },
          testId: 'reversals',
        },
        {
          label: t('sections.supplier_vouchers'),
          to: appRoutesDictionary.supplierVouchers.root,
          testId: 'supplier_vouchers',
        },
      ],
    },
    {
      label: t('sections.operations'),
      icon: TUNDR_ICONS.WRENCH,
      subItems: [
        {
          label: t('sections.suppliers'),
          to: appRoutesDictionary.suppliers.root,
          testId: 'suppliers',
        },
        {
          label: t('sections.reimbursements'),
          to: appRoutesDictionary.reimbursement.root,
          testId: 'reimbursements',
        },
        {
          label: t('sections.bank_transfers'),
          to: appRoutesDictionary.bankTransfers.root,
          testId: 'bank_transfers',
        },
        {
          label: t('sections.over_budget_employees'),
          to: appRoutesDictionary.overBudgetEmployees.root,
          testId: 'over_budget_employees',
        },
        {
          label: t('sections.welfare_assignments'),
          to: appRoutesDictionary.welfareAssignments.root,
          testId: 'welfare_assignments',
        },
        {
          label: t('sections.pension_funds'),
          to: appRoutesDictionary.pensionFunds.root,
          testId: 'pension_funds',
        },
        {
          label: t('sections.health_funds'),
          to: appRoutesDictionary.healthFund.root,
          testId: 'health_funds',
        },
        {
          label: t('sections.vouchers_list'),
          to: appRoutesDictionary.vouchers.root,
          testId: 'vouchers_list',
        },
        {
          label: t('sections.voucher_codes_monitor'),
          to: appRoutesDictionary.voucherCodesMonitor.root,
          testId: 'voucher_codes_monitor',
        },
        {
          label: t('sections.deposits_requests'),
          to: appRoutesDictionary.deposit.root,
          testId: 'deposits_requests',
        },
        {
          label: t('sections.split_files'),
          to: appRoutesDictionary.reseller.root,
          testId: 'split_files',
        },
      ],
    },
  ]);
</script>

<template>
  <PageLayout>
    <template #sidebar>
      <Sidebar
        :variant="SidebarVariant.Light"
        :update-available="needRefresh"
        @update-click="applyUpdate"
      >
        <template #header="{ collapsed }">
          <div :class="sidebarHeaderSectionClass">
            <UserMenu :collapsed="collapsed" />
          </div>
        </template>
        <template #body>
          <SidebarNav :items="items" />
        </template>
      </Sidebar>
    </template>
    <RoutingView />
  </PageLayout>
</template>
